import { UserType } from '@/types'

export interface UserData {
  userType?: UserType
  isRoot?: boolean
  id?: string
  active?: boolean
  email?: string
  fullName?: string
  phone?: string
  profilePicUrl?: string
}

export interface Company {
  id?: string
}

export const isSuperAdmin = (userData?: UserData): boolean => {
  return userData?.userType === UserType.SUPER_ADMIN || userData?.isRoot === true
}

export const isAdmin = (userData?: UserData): boolean => {
  return isSuperAdmin(userData) || userData?.userType === UserType.ADMIN
}

export const isPRManager = (userData?: UserData): boolean => {
  return userData?.userType === UserType.PR_MANAGER
}

export const isPRMember = (userData?: UserData): boolean => {
  return userData?.userType === UserType.PR_MEMBER
}

export const isMediaMember = (userData?: UserData): boolean => {
  return userData?.userType === UserType.MEDIA_MEMBER
}

export const hasCompanyAccess = (userData?: UserData, company?: Company): boolean => {
  return isSuperAdmin(userData) || !!company?.id
}

export const canViewPosts = (userData?: UserData, company?: Company): boolean => {
  return isSuperAdmin(userData) || !!company?.id
}

export const canViewPRMembers = (userData?: UserData, company?: Company): boolean => {
  const hasRole = isSuperAdmin(userData) || isAdmin(userData) || isPRManager(userData) || isPRMember(userData)
  const hasAccess = hasCompanyAccess(userData, company)
  return hasRole && hasAccess
}

export const canViewMediaMembers = (userData?: UserData, company?: Company): boolean => {
  const hasRole = isSuperAdmin(userData) || isAdmin(userData)
  const hasAccess = hasCompanyAccess(userData, company)
  return hasRole && hasAccess
}

export const canViewSupport = (userData?: UserData): boolean => {
  return !!userData?.id
}

export const canViewPrivacyPolicy = (userData?: UserData): boolean => {
  return !!userData?.id
}

export const getCompanyHref = (userData?: UserData, company?: Company): string => {
  if (isSuperAdmin(userData)) {
    return '/companies'
  }

  if (company?.id) {
    return `/companies/${company.id}`
  }

  return '/companies'
}

export interface PRMemberPermissions {
  canInvite: boolean
  canInviteManagers: boolean
  canChangeRole: boolean
  canChangeStatus: boolean
  canResendInvite: boolean
  canViewMembers: boolean
  canPerformActions: boolean
}

export function checkPRMemberPermissions(currentUser?: UserData | null, company?: Company | null): PRMemberPermissions {
  if (!currentUser || !company?.id) {
    return {
      canInvite: false,
      canInviteManagers: false,
      canChangeRole: false,
      canChangeStatus: false,
      canResendInvite: false,
      canViewMembers: false,
      canPerformActions: false,
    }
  }

  const isSuperAdminUser = isSuperAdmin(currentUser)
  const isAdminUser = isAdmin(currentUser)
  const isPRManagerUser = isPRManager(currentUser)

  // Base permissions
  const canViewMembers = isSuperAdminUser || isAdminUser || isPRManagerUser || isPRMember(currentUser)
  const canPerformActions = isSuperAdminUser || isAdminUser || isPRManagerUser

  // Invite permissions
  const canInvite = (isAdminUser || isPRManagerUser) && !isSuperAdminUser
  const canInviteManagers = isAdminUser && !isSuperAdminUser

  // Management permissions
  const canChangeRole = isSuperAdminUser || isAdminUser
  const canChangeStatus = isSuperAdminUser || isAdminUser || isPRManagerUser
  const canResendInvite = canInvite

  return {
    canInvite,
    canInviteManagers,
    canChangeRole,
    canChangeStatus,
    canResendInvite,
    canViewMembers,
    canPerformActions,
  }
}
